import { type JSX, useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { AI_DOC_ANALYZER_DETAILS, AI_DOC_ANALYZER_START } from 'Config/PathTemplates';
import { AiDocAnalysis } from 'Models/ArtificialIntelligence';

export interface AiDocAnalyzerListingProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
}

export const AiDocAnalyzerListing = (props: AiDocAnalyzerListingProps): JSX.Element => {
    const cachedData = useCachedData();

    const [documentAnalyses, setDocumentAnalyses] = useState<AiDocAnalysis[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    useEffect(() => {
        const aiDocAnalyzerGetAll = async (): Promise<void> => {
            try {
                const response = await props.artificialIntelligenceApi.aiDocAnalyzerGetAll();
                setDocumentAnalyses(response.data);
            } catch (error) {
                setLoadingErrorOccurred(error);
            }
        };

        aiDocAnalyzerGetAll();
    }, [props.artificialIntelligenceApi]);

    const columns: ColumnConfig<AiDocAnalysis>[] = [
        {
            type: ColumnType.STRING,
            name: 'Name',
            getString: (aiDocAnalysis) => aiDocAnalysis.input_file?.filename,
            getOnClickTextAction: (aiDocAnalysis) => ({
                goToLocation: generatePath(AI_DOC_ANALYZER_DETAILS, { id: aiDocAnalysis.id }),
            }),
        },
        {
            type: ColumnType.STRING,
            name: 'Type',
            getString: (aiDocAnalysis) => aiDocAnalysis.document_type,
        },
        {
            type: ColumnType.DATE,
            name: 'Created',
            getDateString: (aiDocAnalysis) => aiDocAnalysis.created_timestamp,
        },
        {
            type: ColumnType.USER,
            name: 'Created By',
            getUserId: (aiDocAnalysis) => aiDocAnalysis.created_by,
            users: cachedData.users,
        },
    ];

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (!documentAnalyses) {
        return <Placeholder />;
    }

    return (
        <PageLayoutDefault
            headerTitle="Document Analyzer"
            headerButtons={
                <Link variant="primaryButton" to={AI_DOC_ANALYZER_START} fontAwesomeImage={ICON_ADD_CREATE}>
                    Create Analysis
                </Link>
            }
            body={[
                {
                    content: <Table<AiDocAnalysis> columns={columns} defaultSortColumn="Created" defaultSortDirection="descending" filters={[]} getUniqueIdForRow={(aiDocAnalysis) => aiDocAnalysis.id} rowsData={documentAnalyses} />,
                },
            ]}
        />
    );
};
